import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { NumericFormat } from '@root/shared/core/format'
import { mediaMaxMobile, Separator } from '@root/shared/ui-kit'

import { SplashPointsCard } from './splash-points-card'

import type {
  SplashPointsBreakdown,
  SplashPointsValuesBreakdown,
} from '../atoms'
import type { ClassNameProps } from '@root/shared/react'

type SplashPointsCardsProps = {
  breakdown: SplashPointsBreakdown | undefined
  valuesBreakdown: SplashPointsValuesBreakdown | undefined
} & ClassNameProps

export const SplashPointsCards: React.FC<SplashPointsCardsProps> = ({
  className,
  breakdown,
  valuesBreakdown,
}) => {
  const { t } = useTranslation()

  const renderVolume = () => {
    if (!breakdown || !valuesBreakdown) {
      return <>-</>
    }

    const volumeFormatted = NumericFormat.formatPreset(
      valuesBreakdown.volume,
      'currency-amount-short',
    )

    return (
      <>
        <span className="primary">{breakdown.volumePoints.toFixed()}</span> =
        log(
        <span className="secondary">{volumeFormatted}</span> × 10) × 10
      </>
    )
  }

  const renderSwaps = () => {
    if (!breakdown || !valuesBreakdown) {
      return <>-</>
    }

    const swapsFormatted = NumericFormat.formatPreset(
      valuesBreakdown.swaps,
      'currency-amount-short',
    )

    return (
      <>
        <span className="primary">{breakdown.swapsPoints.toFixed()}</span> =
        log(
        <span className="secondary">{swapsFormatted}</span> × 20) × 2.5
      </>
    )
  }

  const renderMakers = () => {
    if (!breakdown || !valuesBreakdown) {
      return <>-</>
    }

    const makersFormatted = NumericFormat.formatPreset(
      valuesBreakdown.makers,
      'currency-amount-short',
    )

    return (
      <>
        <span className="primary">{breakdown.makersPoints.toFixed()}</span> =
        log(
        <span className="secondary">{makersFormatted}</span> × 20) × 2.5
      </>
    )
  }

  const renderRaids = () => {
    if (!breakdown || !valuesBreakdown) {
      return <>-</>
    }

    const raidPointsFormatted = NumericFormat.format(breakdown.raidPoints, {
      mantissa: 1,
      trimMantissa: true,
    })
    const powerPointsFormatted = NumericFormat.format(
      valuesBreakdown.powerpoints,
      {
        mantissa: 1,
        trimMantissa: true,
      },
    )

    const raidsFormatted = NumericFormat.formatPreset(
      valuesBreakdown.raids,
      'currency-amount-short',
    )
    const raidsEl = <span className="secondary">{raidsFormatted}</span>

    return (
      <>
        <span className="primary">{raidPointsFormatted}</span> = (
        <span className="secondary">{powerPointsFormatted}</span> / (1.6 ×{' '}
        {raidsEl} + 1))² × (1 + 0.01 × {raidsEl})
      </>
    )
  }

  return (
    <Root className={className}>
      <SplashPointsCard
        css={css`
          grid-area: volume;
        `}
      >
        <Title>{t('splash-points.volume')}</Title>
        <Formula>{renderVolume()} </Formula>
        <PointsInfo>
          <span>{t('splash-points.volume-formula')}</span>
        </PointsInfo>
        <SeparatorStyled variant="gradient" opacityMaxPercent={15} />

        <Description>{t('splash-points.volume-description')}</Description>
      </SplashPointsCard>
      <SplashPointsCard
        css={css`
          grid-area: swaps;
        `}
      >
        <Title>{t('splash-points.swaps')}</Title>
        <Formula>{renderSwaps()}</Formula>
        <PointsInfo>
          <span>{t('splash-points.swaps-formula')}</span>
        </PointsInfo>
        <SeparatorStyled variant="gradient" opacityMaxPercent={15} />
        <Description>{t('splash-points.swaps-description')}</Description>
      </SplashPointsCard>
      <SplashPointsCard
        css={css`
          grid-area: makers;
        `}
      >
        <Title>{t('splash-points.makers')}</Title>
        <Formula>{renderMakers()}</Formula>
        <PointsInfo>
          <span>{t('splash-points.makers-formula')}</span>
        </PointsInfo>
        <SeparatorStyled variant="gradient" opacityMaxPercent={15} />
        <Description>{t('splash-points.makers-description')}</Description>
      </SplashPointsCard>
      <SplashPointsCard
        css={css`
          grid-area: raids;
        `}
      >
        <Title>{t('splash-points.raids')}</Title>
        <Formula>{renderRaids()}</Formula>
        <PointsInfo>
          <span>{t('splash-points.raids-formula')}</span>
        </PointsInfo>
        <SeparatorStyled variant="gradient" opacityMaxPercent={15} />
        <Description>{t('splash-points.raids-description')}</Description>
      </SplashPointsCard>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-areas:
    'volume swaps'
    'makers raids';
  gap: 10px;
  margin-bottom: 16px;

  ${mediaMaxMobile(css`
    gap: 12px;
    grid-template-areas:
      'volume'
      'swaps'
      'makers'
      'raids';
    margin-bottom: 36px;
  `)}
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 2px;
  text-align: center;

  ${mediaMaxMobile(css`
    font-size: 14px;
  `)}
`

const Formula = styled.div`
  color: var(--text-11);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;

  .primary {
    color: var(--text-12);
    font-weight: 700;
  }

  .secondary {
    color: var(--text-11, #b4b4b4);
  }

  ${mediaMaxMobile(css`
    font-size: 12px;
  `)}
`

const Description = styled.div`
  color: var(--text-12);
  text-align: center;

  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
`

const SeparatorStyled = styled(Separator)`
  margin-block: 10px 5px;

  ${mediaMaxMobile(css`
    margin-block: 10px;
  `)}
`

const PointsInfo = styled.div`
  color: var(--text-9);
  text-align: center;

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`
